import React, { useState, useEffect } from 'react';
import { useInfiniteQuery, useMutation, InfiniteData, QueryKey } from '@tanstack/react-query'; // Removed unused useQueryClient
import { axiosClientAdmin } from '@/lib/auth';
import { FilterOperator, Comparator } from '@/lib/utils';
import { UserToInvite, UserSearchResponse } from './types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onMemberAdded: () => void;
  onInitiateCreateUser?: (defaults: {
    firstName: string;
    lastName: string;
    email: string;
  }) => void; // Provide defaults when creating a new user
}

async function searchUsers({
  pageParam = 1,
  firstName,
  lastName,
  email,
}: {
  pageParam?: number;
  firstName: string;
  lastName: string;
  email: string;
}): Promise<UserSearchResponse> {
  const params = new URLSearchParams();
  params.append('PageNumber', pageParam.toString());
  params.append('PageSize', '10'); // Show a limited number of users to select from
  // params.append('SortBy', 'name'); // Optional: Add sorting if needed
  // params.append('SortDir', 'Ascending');

  // Overall filter operator for all filter groups
  params.append('Filter.Operator', FilterOperator.And);
  let filterGroupIndex = 0;

  // Filter group for firstName (if provided)
  if (firstName) {
    params.append(`Filter.FilterGroups[${filterGroupIndex}][operator]`, FilterOperator.And);
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][field]`, 'firstName');
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][action]`, Comparator.ILike);
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][value]`, firstName);
    filterGroupIndex++;
  }

  // Filter group for lastName (if provided)
  if (lastName) {
    params.append(`Filter.FilterGroups[${filterGroupIndex}][operator]`, FilterOperator.And);
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][field]`, 'lastName');
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][action]`, Comparator.ILike);
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][value]`, lastName);
    filterGroupIndex++;
  }

  // Filter group for email (if provided)
  if (email) {
    params.append(`Filter.FilterGroups[${filterGroupIndex}][operator]`, FilterOperator.And);
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][field]`, 'email');
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][action]`, Comparator.ILike);
    params.append(`Filter.FilterGroups[${filterGroupIndex}][filters][0][value]`, email);
    filterGroupIndex++;
  }

  const queryString = params.toString();
  const url = `/Users?${queryString}`;
  const response = await axiosClientAdmin.get<UserSearchResponse>(url);
  return response.data;
}

export async function addUserToTeam({
  teamId,
  userId,
}: {
  teamId: string;
  userId: string;
}): Promise<void> {
  const response = await axiosClientAdmin.post(
    `/monitoring/companies/${teamId}/users/${userId}`,
    {}
  );
  return response.data;
}

export function AddTeamMemberModal({
  isOpen,
  onClose,
  teamId,
  onMemberAdded,
  onInitiateCreateUser,
}: AddTeamMemberModalProps) {
  const [firstNameSearch, setFirstNameSearch] = useState('');
  const [lastNameSearch, setLastNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  const [debouncedFirstNameSearch, setDebouncedFirstNameSearch] = useState('');
  const [debouncedLastNameSearch, setDebouncedLastNameSearch] = useState('');
  const [debouncedEmailSearch, setDebouncedEmailSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFirstNameSearch(firstNameSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [firstNameSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLastNameSearch(lastNameSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [lastNameSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmailSearch(emailSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [emailSearch]);

  const {
    data: usersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingUsers,
  } = useInfiniteQuery<
    UserSearchResponse, // TQueryFnData: Data type returned by queryFn
    Error, // TError
    InfiniteData<UserSearchResponse, number>, // TData: Type of 'data' object from the hook
    QueryKey, // TQueryKey: More general QueryKey type
    number // TPageParam: Type for pageParam
  >({
    queryKey: [
      // Specific query key instance
      'searchUsersToAdd',
      debouncedFirstNameSearch,
      debouncedLastNameSearch,
      debouncedEmailSearch,
    ],
    queryFn: (
      { pageParam = 1 } // Default pageParam to 1 if undefined
    ) =>
      searchUsers({
        pageParam,
        firstName: debouncedFirstNameSearch,
        lastName: debouncedLastNameSearch,
        email: debouncedEmailSearch,
      }),
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: UserSearchResponse,
      _allPages: UserSearchResponse[],
      lastPageParam: number
    ) => {
      const pageSize = 10; // This is the PageSize we send in the request params
      if (lastPage.rows.length === pageSize && lastPageParam * pageSize < lastPage.totalRows) {
        // If the last page was full and we haven't fetched all rows yet
        return lastPageParam + 1;
      }
      return undefined;
    },
    enabled: isOpen,
    staleTime: 1000 * 60 * 5, // Cache results for 5 minutes
  });

  const addUserMutation = useMutation<unknown, Error, { teamId: string; userId: string }>({
    mutationFn: addUserToTeam,
    onSuccess: () => {
      onMemberAdded(); // Invalidate queries in parent
      onClose(); // Close modal
      setFirstNameSearch('');
      setLastNameSearch('');
      setEmailSearch('');
      // Optionally, show a success toast
    },
    onError: (error: Error) => {
      // Explicitly type error
      // Optionally, show an error toast
      console.error('Error adding member:', error);
    },
  });

  // Helper booleans for JSX conditional rendering
  const isSearching =
    debouncedFirstNameSearch.length > 0 ||
    debouncedLastNameSearch.length > 0 ||
    debouncedEmailSearch.length > 0;
  const noUsersFoundInResults =
    !isLoadingUsers &&
    (!usersData?.pages || usersData.pages.every((page) => page.rows.length === 0));
  const usersExistInResults =
    !isLoadingUsers && usersData?.pages && usersData.pages.some((page) => page.rows.length > 0);

  const handleAddUser = (userId: string) => {
    addUserMutation.mutate({ teamId, userId });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="border-white/20 bg-[#1c1c1c] text-white sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="text-lime">Add New Member to Team</DialogTitle>
          <div className="text-white/70">
            Search for an existing user by first name, last name, or email to add them to this team.
          </div>
        </DialogHeader>
        <div className="grid gap-4 p-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              id="firstNameSearch"
              placeholder="First Name"
              value={firstNameSearch}
              onChange={(e) => setFirstNameSearch(e.target.value)}
              className="border-white/20 bg-gray-800 focus:border-lime focus:ring-lime"
            />
            <Input
              id="lastNameSearch"
              placeholder="Last Name"
              value={lastNameSearch}
              onChange={(e) => setLastNameSearch(e.target.value)}
              className="border-white/20 bg-gray-800 focus:border-lime focus:ring-lime"
            />
          </div>
          <Input
            id="emailSearch"
            type="email"
            placeholder="Email"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            className="border-white/20 bg-gray-800 focus:border-lime focus:ring-lime"
          />
          <ScrollArea className="h-[450px] w-full rounded-md border border-white/10 bg-gray-800/30 p-2">
            {isLoadingUsers && (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            )}

            {/* Display Users List */}
            {usersExistInResults &&
              usersData?.pages.map((page: UserSearchResponse, pageIndex: number) => (
                <React.Fragment key={pageIndex}>
                  {page.rows.map((user: UserToInvite) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between rounded-md p-2 hover:bg-white/5"
                    >
                      <div>
                        <p className="font-medium text-white/90">{user.name}</p>
                        <p className="text-sm text-white/60">{user.email}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddUser(user.id)}
                        disabled={
                          addUserMutation.isPending && addUserMutation.variables?.userId === user.id
                        }
                        className="border-lime text-lime hover:bg-lime/10 hover:text-lime"
                      >
                        {addUserMutation.isPending &&
                        addUserMutation.variables?.userId === user.id ? (
                          <Spinner size="small" />
                        ) : (
                          'Add'
                        )}
                      </Button>
                    </div>
                  ))}
                </React.Fragment>
              ))}

            {/* Load More Button */}
            {usersExistInResults && hasNextPage && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="border-white/30 text-white/70 hover:bg-white/10 hover:text-white/90"
                >
                  {isFetchingNextPage ? <Spinner size="small" /> : 'Load More'}
                </Button>
              </div>
            )}

            {/* All users loaded message + Create User */}
            {usersExistInResults && !hasNextPage && !isFetchingNextPage && (
              <div className="py-4 text-center text-white/60">
                <p>All matching users loaded.</p>
                {onInitiateCreateUser && (
                  <Button
                    variant="link"
                    onClick={() => {
                      onInitiateCreateUser({
                        firstName: firstNameSearch,
                        lastName: lastNameSearch,
                        email: emailSearch,
                      });
                      onClose();
                    }}
                    className="mt-1 p-0 text-lime hover:text-lime/80"
                  >
                    Create another user?
                  </Button>
                )}
              </div>
            )}

            {/* No Users Found Messages + Create User */}
            {noUsersFoundInResults && (
              <div className="py-4 text-center text-white/60">
                {isSearching ? (
                  <p>No users found matching your search.</p>
                ) : (
                  <p>Type in one or more fields above to search for users.</p>
                )}
                {onInitiateCreateUser && (
                  <Button
                    variant="link"
                    onClick={() => {
                      onInitiateCreateUser({
                        firstName: firstNameSearch,
                        lastName: lastNameSearch,
                        email: emailSearch,
                      });
                      onClose();
                    }}
                    className="mt-1 p-0 text-lime hover:text-lime/80"
                  >
                    Create New User?
                  </Button>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {onInitiateCreateUser && (
            <Button
              variant="outline"
              onClick={() => {
                onInitiateCreateUser({
                  firstName: firstNameSearch,
                  lastName: lastNameSearch,
                  email: emailSearch,
                });
                onClose(); // Close current modal
              }}
            >
              Create New User
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
